import {
  Component,
  OnInit,
  Input,
  ChangeDetectorRef,
  forwardRef,
  Output,
  EventEmitter,
  viewChild,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormsModule,
} from '@angular/forms';
import { CalendarService } from '../services/calendar.service';
import { getFirstDayOfMonth, getDateAsUtc } from '../functions/date-functions';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { ButtonDirective } from '@openng/optimus-ui/button';

@Component({
  selector: 'app-datepicker',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ButtonDirective, DatePicker, FormsModule],
})
export class CalendarComponent implements OnInit, ControlValueAccessor {
  private _calendarService = inject(CalendarService);
  private _cdRef = inject(ChangeDetectorRef);

  @Input() showPrevNext = false;
  readonly defaultDate = input<Date>();
  readonly selectionMode = input<'single' | 'multiple' | 'range'>('single');
  readonly style = input<string>();
  readonly styleClass = input<string>();
  readonly inputStyle = input<string>();
  readonly inputStyleClass = input<string>();
  readonly name = input<string>();
  readonly placeholder = input('mm/dd/yyyy');
  @Input() isDisabled = false;
  readonly dateFormat = input('mm/dd/yy');
  readonly inline = input(false);
  readonly showOtherMonths = input(true);
  readonly selectOtherMonths = input(false);
  readonly showIcon = input(true);
  readonly showOnFocus = input(true);
  readonly icon = input('fa fa-calendar');
  readonly appendTo = input<any>(undefined);
  readonly readOnlyInput = input<boolean>();
  readonly shortYearCutoff = input('+10');
  readonly minDate = input<Date>();
  readonly maxDate = input<Date>();
  readonly disabledDates = input<Array<Date>>();
  readonly disabledDays = input<Array<number>>();
  readonly showTime = input(false);
  readonly hourFormat = input('24');
  readonly timeOnly = input(false);
  readonly dataType = input('date');
  readonly required = input(false);
  readonly tabindex = input<number>();
  readonly showSeconds = input(false);
  readonly stepHour = input(1);
  readonly stepMinute = input(1);
  readonly stepSecond = input(1);
  readonly utc = input(true);
  readonly todayButtonStyleClass = input('ui-secondary-button');
  readonly clearButtonStyleClass = input('ui-secondary-button');
  readonly baseZIndex = input(0);
  readonly autoZIndex = input(false);
  readonly panelStyleClass = input<string>();
  readonly keepInvalid = input(false);
  readonly hideOnDateTimeSelect = input(false);
  @Input() changeByMonth = false;

  @Output() selected = new EventEmitter<any>();
  calendarDate!: Date | null;
  inFocus = false;

  readonly control = viewChild(DatePicker);

  propagateChange: any = () => {};
  propagateTouch: any = () => {};

  writeValue(obj: any): void {
    if (obj) {
      const date: Date = obj;
      this.calendarDate = getDateAsUtc(date);
    } else {
      this.calendarDate = null;
    }
    // Have to reset the current month/year in case the user has used the
    // overlay to move the calendar.
    if (this.calendarDate) {
      const control = this.control()!;
      control.currentMonth = this.calendarDate.getMonth();
      control.currentYear = this.calendarDate.getFullYear();
    }
    this._cdRef.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  // Allows Angular to disable the input.
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnInit() {
    const defaultDate = this.defaultDate();
    if (defaultDate) {
      this.calendarDate = new Date(defaultDate);
    }
  }

  /**
   * This action occurs when a date is entered in the date input box
   */
  onModelChange($event: any) {
    if (!this.inFocus) {
      this.processEvent($event);
    }
  }

  /**
   * This action occurs when a date is entered in the date input box
   */
  onBlur($event: Event) {
    this.inFocus = false;
    const d = ($event.target as HTMLInputElement).value;
    this.processEvent(d);
  }

  onFocus() {
    this.inFocus = true;
  }

  private processEvent($event: any): void {
    if (
      (!isNullOrUndefined($event) &&
        $event !== '' &&
        isNullOrUndefined(this.calendarDate)) ||
      (this.calendarDate &&
        new Date($event).valueOf() !== this.calendarDate.valueOf())
    ) {
      if (this.showTime()) {
        this.writeValue(this._calendarService.getValidDateTime($event));
      } else {
        const date = this._calendarService.getValidDate($event);
        if (date) {
          date.setHours(0, 0, 0, 0);
        }
        this.writeValue(date);
      }
      this.setCalendar();
    } else if (
      isNullOrUndefined($event) &&
      !isNullOrUndefined(this.calendarDate)
    ) {
      this.calendarDate = null;
      this.setCalendar();
    }
  }

  /**
   * This action occurs when previous button is selected
   */
  previousDateEvent() {
    const cur = new Date(this.calendarDate!);
    let date: number;
    if (this.changeByMonth) {
      date = cur.setMonth(this.calendarDate!.getMonth() - 1);
      this.calendarDate = getFirstDayOfMonth(new Date(date));
    } else {
      date = cur.setDate(this.calendarDate!.getDate() - 1);
      this.calendarDate = new Date(date);
    }

    this.setCalendar();
  }

  /**
   * This action occurs when next button is selected
   */
  nextDateEvent() {
    const cur = new Date(this.calendarDate!);
    let date: number;
    if (this.changeByMonth) {
      date = cur.setMonth(this.calendarDate!.getMonth() + 1);
      this.calendarDate = getFirstDayOfMonth(new Date(date));
    } else {
      date = cur.setDate(this.calendarDate!.getDate() + 1);
      this.calendarDate = new Date(date);
    }

    this.setCalendar();
  }

  /**
   * This action occurs when a date is selected from popup calendar
   */
  onSelect($event: Date) {
    this.calendarDate = $event;
    this.setCalendar();
  }

  setCalendar() {
    this.propagateTouch();
    this.propagateChange(this.calendarDate);
    this.selected.emit(this.calendarDate);
    const control = this.control()!;
    control.overlayVisible = false; // need to hide the overlay when we are setting the calendar.
    // Have to reset the current month/year in case the user has used the
    // overlay to move the calendar.
    if (this.calendarDate) {
      control.currentMonth = this.calendarDate.getMonth();
      control.currentYear = this.calendarDate.getFullYear();
    }
  }
}
